import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar, Mail, Phone, MapPin, ArrowRight, Send, MessageSquare } from 'lucide-react';

import Navigation from './Navigation';
import { Link, useNavigate } from "react-router-dom";
import tulioIcon from './assets/tulio.png'

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Thank you for your interest! We will contact you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      company: '',
      message: '',
      service: ''
    });
  };

  const services = [
    'Full Product Development',
    'AI Consulting & Agent Development',
    'Technology Consulting',
    'Staff Augmentation',
    'Data Engineering & ML',
    'Growth & Capital Support'
  ];

    

  return (
    <>
  {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Get Started</Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Let's discuss your project and explore how Tulio Labs can help you achieve your technology goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Book a Strategy Call</h3>
                <p className="text-slate-600">Tell us about your project and we'll get back to you within 24 hours.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Full Name</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Email Address</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@company.com"
                      required
                      className="border-slate-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Company</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your Company Name"
                    className="border-slate-300"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Service Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Select a service...</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Project Details</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project, timeline, and goals..."
                    rows={4}
                    className="border-slate-300"
                  />
                </div>

                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-3">
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full text-lg py-3"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Call Instead
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <Calendar className="w-8 h-8 text-emerald-600 mr-3" />
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Schedule a Call</h3>
                    <p className="text-slate-600">Book a 30-minute strategy session</p>
                  </div>
                </div>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  Book Calendar Slot
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Get in Touch</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-emerald-600 mr-3" />
                    <div>
                      <p className="font-semibold text-slate-900">Email</p>
                      <p className="text-slate-600">hello@tuliolabs.com</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-emerald-600 mr-3" />
                    <div>
                      <p className="font-semibold text-slate-900">Phone</p>
                      <p className="text-slate-600">+65 8123 4567</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-emerald-600 mr-3" />
                    <div>
                      <p className="font-semibold text-slate-900">Singapore HQ</p>
                      <p className="text-slate-600">One Raffles Place, Singapore</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <MessageSquare className="w-8 h-8 text-emerald-600 mr-3" />
                  <h3 className="text-xl font-bold text-slate-900">Why Choose Tulio Labs?</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-slate-700">30% faster delivery than market average</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-slate-700">25-40% cost savings vs in-house teams</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-slate-700">Global engineering centers for 24/7 support</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-slate-700">AI-first approach to all solutions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>

    {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
             <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ContactSection;